import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCTS = [
    {
        id: "product_fate_return_mirror",
        testid: "product-mirror",
        name: "Mara's Fate Return Mirror",
        tagline: "Some bonds always find the way back.",
        desc: "The complete return working: antique-finished ritual mirror, binding cord, white candle, wax-sealed working and Mara's instruction card — reflect, release, return.",
        img: "/assets/products/fate-return-mirror.jpg",
    },
    {
        id: "product_three_currents",
        testid: "product-candles",
        name: "The Three Currents Candle Set",
        tagline: "White. Grey. Black.",
        desc: "Three hand-poured currents in one coffret — light the white to cleanse, the grey to balance, the black to sever what should never have attached.",
        img: "/assets/products/three-currents.jpg",
    },
    {
        id: "product_crossed_path_oil",
        testid: "product-oil",
        name: "Crossed Path Ritual Oil",
        tagline: "For the hour two roads entwine.",
        desc: "Mara's own blended oil for anointing candles, letters and doorways — for when a path that was yours began carrying someone else.",
        img: "/assets/products/crossed-path-oil.jpg",
    },
    {
        id: "product_veilbreaker",
        testid: "product-veilbreaker",
        name: "The Veilbreaker Pendant",
        tagline: "Worn against interference.",
        desc: "A dark mirror pendant in gilded filigree — a ward carried at the throat while the reversal is undone.",
        img: "/assets/products/veilbreaker.jpg",
    },
];

export const ShopSection = () => {
    const [buying, setBuying] = useState(null);

    const buy = async (p) => {
        setBuying(p.id);
        try {
            const res = await axios.post(`${API}/payments/paypal/product`, {
                product_id: p.id,
                origin_url: window.location.origin,
            });
            window.location.href = res.data.checkout_url;
        } catch (err) {
            if (err?.response?.status === 503) {
                toast.error("The payment mirror is not yet configured — Mara will take your order by hand soon.");
            } else {
                toast.error("The payment mirror clouded. Try again.");
            }
            setBuying(null);
        }
    };

    return (
        <section id="collection" data-testid="collection-section" className="relative px-5 py-28 sm:px-8 lg:py-36">
            <div className="nebula-blob right-[0%] top-[10%] h-[28rem] w-[28rem] bg-[#5a189a]/25" />
            <div className="nebula-blob bottom-[5%] left-[5%] h-96 w-96 bg-[#2e0b53]/40" />
            <div className="mx-auto max-w-7xl">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9 }}
                    className="mb-16 max-w-2xl"
                >
                    <p className="eyebrow mb-4">Chapter IX · The Ritual Collection</p>
                    <h2 className="text-glow-lilac font-display text-2xl font-bold tracking-wide text-[#f8f7ff] sm:text-3xl lg:text-4xl">
                        OBJECTS THAT REMEMBER
                    </h2>
                    <p className="mt-6 font-cormorant text-lg italic leading-relaxed text-[#e0aaff]/80">
                        Physical workings prepared under Mara's own hand. Each piece is $69.99, sealed, and sent with its
                        instructions — worldwide.
                    </p>
                </motion.div>

                <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
                    {PRODUCTS.map((p, i) => (
                        <motion.article
                            key={p.id}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.8, delay: i * 0.1 }}
                            data-testid={p.testid}
                            className="glass-card glass-card-hover group relative flex flex-col overflow-hidden"
                        >
                            <div className="relative aspect-square overflow-hidden">
                                <img
                                    src={p.img}
                                    alt={p.name}
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0b031e] via-transparent to-transparent" />
                            </div>
                            <div className="flex flex-1 flex-col p-6">
                                <h3 className="font-cinzel text-base font-semibold leading-snug tracking-wide text-[#f8f7ff]">
                                    {p.name}
                                </h3>
                                <p className="mt-1 font-cormorant text-sm italic text-[#c77dff]/80">{p.tagline}</p>
                                <p className="mt-3 flex-1 text-sm leading-relaxed text-[#f8f7ff]/75">{p.desc}</p>
                                <div className="mt-5 flex items-center justify-between">
                                    <p className="font-display text-2xl font-bold text-[#e0aaff] text-glow-lilac">$69.99</p>
                                    <button
                                        type="button"
                                        onClick={() => buy(p)}
                                        disabled={!!buying}
                                        data-testid={`buy-${p.testid}`}
                                        className="btn-ghost-glow !px-5 !py-2.5 disabled:opacity-60"
                                    >
                                        {buying === p.id ? "OPENING…" : "BUY NOW"}
                                    </button>
                                </div>
                            </div>
                        </motion.article>
                    ))}
                </div>
            </div>
        </section>
    );
};
